import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'redux';
import classNames from 'classnames';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';
import { propTypes } from '../../../util/types';
import {
    queryReviewsReceived,
    queryReviewsGiven,
    STORE_PROFILE_REVIEWS_PER_PAGE,
} from '../StoreProfilePage.duck';
import ReviewsReceivedView from './ReviewsReceivedView/ReviewsReceivedView';
import ReviewsGivenView from './ReviewsGivenView/ReviewsGivenView';
import css from './ReviewsTab.module.css';

/**
 * ReviewsTab component manages sub-tabs and data fetching for reviews
 */
const ReviewsTab = ({
    userId,
    achievementReviews,
    reviewSummary,
    // Reviews Received
    reviewsReceived,
    totalReviewsReceived,
    reviewsReceivedInProgress,
    reviewsReceivedError,
    reviewsReceivedPagination,
    // Reviews Given
    reviewsGiven,
    totalReviewsGiven,
    reviewsGivenInProgress,
    reviewsGivenError,
    reviewsGivenPagination,
    // Actions
    onQueryReviewsReceived,
    onQueryReviewsGiven,
    intl,
}) => {
    const [activeSubTab, setActiveSubTab] = useState('received');
    const [receivedPage, setReceivedPage] = useState(1);
    const [givenPage, setGivenPage] = useState(1);

    const hasSSRReceivedPage1 =
        activeSubTab === 'received' &&
        receivedPage === 1 &&
        Array.isArray(reviewsReceived) &&
        reviewsReceived.length > 0 &&
        reviewsReceivedPagination?.page === 1;

    const hasSSRGivenPage1 =
        activeSubTab === 'given' &&
        givenPage === 1 &&
        Array.isArray(reviewsGiven) &&
        reviewsGiven.length > 0 &&
        reviewsGivenPagination?.page === 1;

    // Fetch reviews when sub-tab or page changes
    useEffect(() => {
        if (!userId) return;

        if (hasSSRReceivedPage1 || hasSSRGivenPage1) {
            return;
        }

        if (activeSubTab === 'received') {
            onQueryReviewsReceived(
                userId,
                receivedPage,
                STORE_PROFILE_REVIEWS_PER_PAGE
            );
        } else {
            onQueryReviewsGiven(userId, givenPage, 10);
        }
    }, [
        activeSubTab,
        receivedPage,
        givenPage,
        userId,
        hasSSRReceivedPage1,
        hasSSRGivenPage1,
        onQueryReviewsReceived,
        onQueryReviewsGiven
    ]);

    const handleSubTabChange = useCallback((tab) => {
        setActiveSubTab(tab);
        // Reset pagination when switching tabs
        if (tab === 'received') {
            setReceivedPage(1);
        } else {
            setGivenPage(1);
        }
    }, []);

    const handleLoadMoreReceived = useCallback(() => {
        if (reviewsReceivedPagination && reviewsReceivedPagination.page < reviewsReceivedPagination.totalPages) {
            setReceivedPage(prev => prev + 1);
        }
    }, [reviewsReceivedPagination]);

    const handleLoadMoreGiven = useCallback(() => {
        if (reviewsGivenPagination && reviewsGivenPagination.page < reviewsGivenPagination.totalPages) {
            setGivenPage(prev => prev + 1);
        }
    }, [reviewsGivenPagination]);

    const currentReviewsReceived = reviewsReceived || [];
    const currentReviewsGiven = reviewsGiven || [];
    const summaryReviewCount = Number(reviewSummary?.reviewCount);
    const achievementReviewCount = Array.isArray(achievementReviews)
        ? achievementReviews.length
        : 0;
    const baseReceivedCount =
        Number.isFinite(summaryReviewCount) && summaryReviewCount > 0
            ? Math.floor(summaryReviewCount)
            : totalReviewsReceived;
    const displayReceivedCount =
        Number(baseReceivedCount || 0) + achievementReviewCount;

    return (
        <div className={css.reviewsTab}>
            {/* Sub-tab Content */}
            {activeSubTab === 'received' ? (
                <ReviewsReceivedView
                    reviews={currentReviewsReceived}
                    achievementReviews={achievementReviews}
                    totalReviews={totalReviewsReceived}
                    reviewSummary={reviewSummary}
                    inProgress={reviewsReceivedInProgress}
                    error={reviewsReceivedError}
                    intl={intl}
                    onLoadMore={handleLoadMoreReceived}
                    pagination={reviewsReceivedPagination}
                    activeSubTab={activeSubTab}
                    totalReviewsReceived={displayReceivedCount}
                    totalReviewsGiven={totalReviewsGiven}
                    onSubTabChange={handleSubTabChange}
                />
            ) : (
                <ReviewsGivenView
                    reviews={currentReviewsGiven}
                    totalReviews={totalReviewsGiven}
                    achievementReviews={achievementReviews}
                    inProgress={reviewsGivenInProgress}
                    error={reviewsGivenError}
                    intl={intl}
                    onLoadMore={handleLoadMoreGiven}
                    pagination={reviewsGivenPagination}
                    activeSubTab={activeSubTab}
                    totalReviewsReceived={displayReceivedCount}
                    totalReviewsGiven={totalReviewsGiven}
                    onSubTabChange={handleSubTabChange}
                />
            )}
        </div>
    );
};

ReviewsTab.propTypes = {
    userId: propTypes.uuid,
    reviewSummary: PropTypes.shape({
        averageRating: PropTypes.number,
        reviewCount: PropTypes.number,
        starCounts: PropTypes.shape({
            1: PropTypes.number,
            2: PropTypes.number,
            3: PropTypes.number,
            4: PropTypes.number,
            5: PropTypes.number,
        }),
    }),
    // Reviews Received
    reviewsReceived: PropTypes.arrayOf(propTypes.review),
    totalReviewsReceived: PropTypes.number,
    reviewsReceivedInProgress: PropTypes.bool,
    reviewsReceivedError: propTypes.error,
    reviewsReceivedPagination: PropTypes.shape({
        page: PropTypes.number,
        perPage: PropTypes.number,
        totalPages: PropTypes.number,
    }),
    // Reviews Given
    reviewsGiven: PropTypes.arrayOf(propTypes.review),
    totalReviewsGiven: PropTypes.number,
    reviewsGivenInProgress: PropTypes.bool,
    reviewsGivenError: propTypes.error,
    reviewsGivenPagination: PropTypes.shape({
        page: PropTypes.number,
        perPage: PropTypes.number,
        totalPages: PropTypes.number,
    }),
    // Actions
    onQueryReviewsReceived: PropTypes.func.isRequired,
    onQueryReviewsGiven: PropTypes.func.isRequired,
    intl: intlShape.isRequired,
};

const mapStateToProps = (state) => {
    const {
        userId,
        reviewsReceived,
        totalReviewsReceived,
        reviewsReceivedInProgress,
        reviewsReceivedError,
        reviewsReceivedPagination,
        reviewsGiven,
        totalReviewsGiven,
        reviewsGivenInProgress,
        reviewsGivenError,
        reviewsGivenPagination,
    } = state.StoreProfilePage;

    return {
        userId,
        reviewsReceived,
        totalReviewsReceived,
        reviewsReceivedInProgress,
        reviewsReceivedError,
        reviewsReceivedPagination,
        reviewsGiven,
        totalReviewsGiven,
        reviewsGivenInProgress,
        reviewsGivenError,
        reviewsGivenPagination,
    };
};

const mapDispatchToProps = (dispatch) => ({
    onQueryReviewsReceived: (userId, page, perPage) =>
        dispatch(queryReviewsReceived(userId, page, perPage)),
    onQueryReviewsGiven: (userId, page, perPage) =>
        dispatch(queryReviewsGiven(userId, page, perPage)),
});

export default compose(
    connect(mapStateToProps, mapDispatchToProps),
    injectIntl
)(ReviewsTab);
